import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { usePasswordReset } from "../../../hooks/usePasswordReset";
import useFormValidation from "../../../hooks/useFormValidation";

/**
 * Password input field with visibility toggle
 */
const PasswordField = ({ id, label, value, onChange, error, placeholder }) => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="space-y-1.5 sm:space-y-2">
      <label htmlFor={id} className="block text-xs sm:text-sm font-semibold text-gray-700">
        {label}
      </label>
      <div className="relative">
        <input
          type={showPassword ? "text" : "password"}
          id={id}
          name={id}
          value={value}
          onChange={onChange}
          required
          placeholder={placeholder}
          className={`
            w-full px-3 sm:px-4 py-3 sm:py-3.5 pr-10 sm:pr-12 text-sm sm:text-base border-2 rounded-xl
            bg-white text-gray-900 placeholder-gray-400
            focus:outline-none focus:ring-2 transition-all duration-200
            ${error 
              ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20' 
              : 'border-gray-200 focus:border-primary focus:ring-primary/20'
            }
          `}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 p-1 sm:p-1.5 
                     text-gray-400 hover:text-gray-600 transition-colors"
          aria-label={showPassword ? "Hide password" : "Show password"}
        >
          {showPassword ? <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" /> : <Eye className="w-4 h-4 sm:w-5 sm:h-5" />}
        </button>
      </div>
      {error && (
        <p className="text-[10px] sm:text-sm text-red-600 flex items-center gap-1 sm:gap-1.5">
          <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
          {error}
        </p>
      )}
    </div>
  );
};

const PasswordResetForm = () => {
  const { t } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ password: "", confirmPassword: "" });
  const [successMessage, setSuccessMessage] = useState("");
  const { handleResetPassword, resetLoading, resetError } = usePasswordReset();

  const { isFormValid, errors } = useFormValidation(formData);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormValid) {
      return;
    }

    try {
      await handleResetPassword(token, formData.password);
      setSuccessMessage(t("password_reset_success", "Your password has been successfully reset!"));
      
      // Redirect to login after success
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      console.error("Error resetting password:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-3 sm:p-4">
      <motion.div
        className="w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl p-5 sm:p-8">
          {/* Header */}
          <div className="text-center mb-6 sm:mb-8">
            <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Lock className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1.5 sm:mb-2">
              {t("reset_password", "Reset Password")}
            </h1>
            <p className="text-gray-600 text-xs sm:text-sm">
              {t("enter_new_password", "Enter your new password below.")}
            </p>
          </div>

          {/* Success Message */}
          {successMessage ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <CheckCircle className="w-8 h-8 sm:w-10 sm:h-10 text-green-600" />
              </div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1.5 sm:mb-2">
                {t("success", "Success!")}
              </h2>
              <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{successMessage}</p>
              <p className="text-[10px] sm:text-sm text-gray-500">
                {t("redirecting_to_login", "Redirecting to login...")}
              </p>
            </motion.div>
          ) : (
            /* Form */
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">
              <PasswordField
                id="password"
                label={t("new_password", "New Password")}
                value={formData.password}
                onChange={handleChange}
                error={errors.password}
                placeholder={t("enter_new_password_placeholder", "Enter new password")}
              />
              
              <PasswordField
                id="confirmPassword"
                label={t("confirm_new_password", "Confirm New Password")}
                value={formData.confirmPassword}
                onChange={handleChange}
                error={errors.confirmPassword}
                placeholder={t("confirm_password_placeholder", "Confirm your password")}
              />

              {/* Error Message */}
              {resetError && (
                <div className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-red-50 rounded-xl">
                  <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-xs sm:text-sm text-red-800 font-medium">
                    {t("error_resetting_password", "Error resetting password. Please try again.")}
                  </p>
                </div>
              )}

              <motion.button
                type="submit"
                disabled={!isFormValid || resetLoading}
                className={`
                  w-full py-3 sm:py-3.5 px-4 sm:px-6 rounded-xl font-semibold text-white text-sm sm:text-base
                  transition-all duration-200 flex items-center justify-center gap-2
                  ${!isFormValid || resetLoading
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90 active:scale-[0.98] shadow-lg'
                  }
                `}
                whileHover={isFormValid && !resetLoading ? { scale: 1.01 } : undefined}
                whileTap={isFormValid && !resetLoading ? { scale: 0.98 } : undefined}
              >
                {resetLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    <span>{t("resetting", "Resetting...")}</span>
                  </>
                ) : (
                  t("reset_password", "Reset Password")
                )}
              </motion.button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default PasswordResetForm;
